import React, { useEffect, useRef, useState } from 'react';
import { FeaturePackItemConfig } from '../types';

interface FeaturePackProps {
  visible: boolean;
  packName?: string;
  items: FeaturePackItemConfig[];
}


/** Rough height per detailed pill row (padding + grid content + gap). */
const DETAILED_PILL_HEIGHT = 100;
/** Header + padding overhead for the feature pack section. */
const FP_OVERHEAD = 60;

export const FeaturePack: React.FC<FeaturePackProps> = ({ visible, packName = 'Edge', items = [] }) => {
  const [isDetailed, setIsDetailed] = useState(false);
  const [autoExpanded, setAutoExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-expand when the feature pack becomes visible if the screen is tall enough
  useEffect(() => {
    if (!visible || autoExpanded || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const availableHeight = window.innerHeight - rect.top;
    const neededHeight = FP_OVERHEAD + items.length * DETAILED_PILL_HEIGHT;

    if (availableHeight >= neededHeight) {
      setIsDetailed(true);
      setAutoExpanded(true);
    }
  }, [visible, items.length, autoExpanded]);

  return (
    <div ref={containerRef} className={`ms-feature-pack${visible ? ' revealed' : ''}`}>
      <div className="ms-fp-header">
        <div className="ms-fp-title">
          <span>{packName}</span> feature pack includes
        </div>
        <div 
          className="ms-fp-link"
          onClick={() => setIsDetailed(!isDetailed)}
        >
          {isDetailed ? 'Show less' : 'See detailed view'}
        </div>
      </div>
      <div className={`ms-fp-row${isDetailed ? ' detailed' : ''}`}>
        {items.map((pill) => (
          <div key={pill.label} className={`ms-fp-pill${isDetailed ? ' detailed' : ''}`}>
            
            <div className="ms-fp-info">
              <div className="ms-fp-icon">
                {pill.icon && <img src={pill.icon} alt={pill.label} />}
              </div>
              <div className="ms-fp-title-col">
                <span className="ms-fp-label">{pill.label}</span>
                <span className="ms-fp-level">{pill.level}</span>
              </div>
            </div>
            
            <div className="ms-fp-track-container">
              <div className="ms-fp-track-header">You vs Market</div>
              <div className="ms-fp-track-bg">
                {/* Market hashed fill */}
                <div 
                  className="ms-fp-track-fill ms-fp-track-fill--market" 
                  style={{ width: isDetailed ? `${pill.marketPin}%` : '0%' }}
                />
                {/* Your level fill */}
                <div 
                  className="ms-fp-track-fill ms-fp-track-fill--you" 
                  style={{ width: isDetailed ? `${pill.progress}%` : '0%' }}
                />
                {pill.progress === pill.marketPin ? (
                  /* Same level — single combined pin */
                  <div
                    className="ms-fp-pin ms-fp-pin--combined"
                    style={{ left: isDetailed ? `${pill.progress}%` : '0%' }}
                    data-edge={pill.progress >= 100 ? 'right' : pill.progress <= 0 ? 'left' : undefined}
                  >
                    <div className="ms-fp-pin-label ms-fp-pin-label--combined">
                      {/* {pill.pinLabel} */}
                    </div>
                    <div className="ms-fp-pin-below">
                      <div className="ms-fp-pin-arrow" />
                      <div className="ms-fp-pin-text">You and Market</div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Your level pin */}
                    <div
                      className="ms-fp-pin ms-fp-pin--you"
                      style={{ left: isDetailed ? `${pill.progress}%` : '0%' }}
                      data-edge={pill.progress >= 100 ? 'right' : pill.progress <= 0 ? 'left' : undefined}
                    >
                      <div className="ms-fp-pin-label ms-fp-pin-label--you">
                        {/* {pill.pinLabel} */}
                      </div>
                      <div className="ms-fp-pin-below">
                        <div className="ms-fp-pin-arrow" />
                        <div className="ms-fp-pin-text">You</div>
                      </div>
                    </div>
                    {/* Market average pin */}
                    <div
                      className="ms-fp-pin ms-fp-pin--market"
                      style={{ left: isDetailed ? `${pill.marketPin}%` : '0%' }}
                      data-edge={pill.marketPin >= 100 ? 'right' : pill.marketPin <= 0 ? 'left' : undefined}
                    >
                      {/* <div className="ms-fp-pin-label ms-fp-pin-label--market">{pill.marketPinLabel}</div> */}
                      <div className="ms-fp-pin-below">
                        <div className="ms-fp-pin-arrow" />
                        <div className="ms-fp-pin-text">Market</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="ms-fp-comparison">
              <span className="ms-fp-comp-label">In comparison with market</span>
              <span className="ms-fp-comp-value">{pill.comparison}</span>
            </div>

          </div>
        ))}
      </div>
    </div>
  );
};
